/* eslint-disable max-len */
/* eslint-disable max-lines */
import {
  ArrowPathIcon,
  CheckCircleIcon,
  CheckIcon,
  EnvelopeIcon,
  PencilIcon,
  PresentationChartLineIcon,
  XMarkIcon
} from "@heroicons/react/24/outline";
import { useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { useSearch } from "../../hooks/useSearch";
import { IEsimReservation } from "../../interfaces/esim/EsimReservations.types";
import {
  ESIM_STATUS_KEYS,
  getEsimProfileStatusColor,
  getEsimProfileStatusDescription,
  getEsimProfileStatusLabel
} from "../../utils/esim/esimStatus";
import {
  getEsimReservations,
  resendEsimReservationEmail,
  updateEsimProfileEmail
} from "../../services/Esim.service";
import FilteredListState, { ResultCount } from "../reservation_v2/filters/FilteredListState";
import { RESERVATION_STATUS_OPTIONS } from "../reservation_v2/filters/reservationFilters";
import useFilterParams from "../reservation_v2/filters/useFilterParams";
import SkeletonTicket from "../reservation_v2/SkeletonTicket";
import Button from "../ui/Button";
import DeleteModal from "../ui/DeleteModal";
import IconBtn from "../ui/IconBtn";
import Modal from "../ui/Modal";
import Pagination from "../ui/table/Pagination";
import SuperAdmin from "../users/permissions/SuperAdmin";
import CancelEsimModal from "./CancelEsimModal";
import { ESIM_FILTER_KEY_LIST, ESIM_FILTER_KEYS as KEYS } from "./filters/esimReservationFilters";

const EsimReservations = () => {
  // useSearch reads the search, status and date range itself; the other filters ride along in
  // otherQuery, so any change to them refires the fetch. EsimFilterBar clears `pageindex` on every
  // change, so the list returns to page 1.
  const { get: param, hasFilters, clearAll } = useFilterParams(ESIM_FILTER_KEY_LIST);

  const { response, loading, error: listError, handlePageChange, page, refetch } = useSearch(
    getEsimReservations,
    {
      withQuerySearch: true,
      filterSelector: KEYS.status,
      withFilter: true,
      withDateFrom: true,
      withDateTo: true,
      resetResponseOnError: true,
      otherQuery: {
        pageSize: "10",
        locationZoneId: param(KEYS.zoneId),
        packageTemplateId: param(KEYS.packageId),
        paymentMethodId: param(KEYS.paymentMethodId),
        channelGroups: param(KEYS.channels),
        isTopUp: param(KEYS.type),
        esimStatus: param(KEYS.esimStatus),
        withBusReservation: param(KEYS.bus)
      }
    }
  );

  const [editingProfileId, setEditingProfileId] = useState<number | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [showStatusLegend, setShowStatusLegend] = useState(false);
  const [currentReservationId, setCurrentReservationId] = useState<
    number | null
  >(null);
  const [isResendingEmail, setIsResendingEmail] = useState(false);

  // Reservation Status (Payment/Order status), worded as on the status tabs.
  const getStatusLabel = (status: number) =>
    RESERVATION_STATUS_OPTIONS.find((o) => o.value === `${status}`)?.label || "E panjohur";

  const getStatusColor = (status: number) => {
    switch (status) {
      case 1:
        return "bg-green text-white";
      case 2:
        return "bg-orange text-white";
      case 3:
        return "bg-red text-white";
      case 4:
        return "bg-blue text-white";
      default:
        return "bg-gray-200";
    }
  };

  const handleEditEmail = (esimProfileId: number, currentEmail: string) => {
    setEditingProfileId(esimProfileId);
    setNewEmail(currentEmail);
  };

  const handleCancelEdit = () => {
    setEditingProfileId(null);
    setNewEmail("");
  };

  const handleUpdateEmail = async (
    esimProfileId: number,
    reservationId: number,
    oldEmail: string
  ) => {
    if (!newEmail || newEmail === oldEmail) {
      handleCancelEdit();
      return;
    }

    setIsUpdatingEmail(true);
    try {
      await updateEsimProfileEmail(esimProfileId, newEmail);
      toast.success("Email-i u përditësua me sukses");

      setCurrentReservationId(reservationId);
      setEditingProfileId(null);
      setNewEmail("");
      setShowResendModal(true);

      refetch();
    } catch (error) {
      toast.error("Ndodhi një gabim gjatë përditësimit të email-it");
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const handleResendEmail = async () => {
    if (!currentReservationId) return;

    setIsResendingEmail(true);
    try {
      await resendEsimReservationEmail(currentReservationId);
      toast.success("Email-i u dërgua përsëri me sukses");
      setShowResendModal(false);
    } catch (error) {
      toast.error("Ndodhi një gabim gjatë dërgimit të email-it");
    } finally {
      setIsResendingEmail(false);
    }
  };

  const handleCloseModal = () => {
    setShowResendModal(false);
    setCurrentReservationId(null);
  };

  return (
    <>
      <div className="flex flex-col gap-4 lg:max-w-[75%] mx-auto">
        <ResultCount
          loading={loading}
          count={response?.data.totalCount}
          noun="rezervime eSIM"
          aside={(
            <Button
              onClick={() => setShowStatusLegend(true)}
              btnType="custom"
              className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm
                text-neutral hover:bg-gray-50 hover:text-black"
            >
              <PresentationChartLineIcon className="w-4 h-4" />
              Shiko statuset
            </Button>
          )}
        />

        <div className="flex gap-4 flex-col w-full items-center">
          <SkeletonTicket loading={loading} />
          {!loading &&
            response?.data.result.map((el: IEsimReservation) => (
              <div
                key={el.uniqueReservationGuid}
                className="w-full bg-white border border-gray-200 p-4 rounded-lg hover:shadow-md
                transition-shadow duration-200"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold">
                        {el.firstName} {el.lastName}
                      </h3>
                      <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                        ID: {el.id}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingProfileId === el.esimProfileId ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            className="text-gray-600 text-sm bg-white border border-gray-300
                              rounded px-2 py-1 min-w-[200px]"
                            autoFocus
                            disabled={isUpdatingEmail}
                          />
                          <IconBtn
                            onClick={
                              isUpdatingEmail
                                ? undefined
                                : () =>
                                    handleUpdateEmail(
                                      el.esimProfileId!,
                                      el.id,
                                      el.email
                                    )
                            }
                            className={`w-6 h-6 ${
                              isUpdatingEmail ? "cursor-not-allowed" : ""
                            }`}
                          >
                            {isUpdatingEmail ? (
                              <ArrowPathIcon className="w-4 h-4 text-gray-500 animate-spin" />
                            ) : (
                              <CheckIcon className="w-4 h-4 text-green-500 hover:text-green-600" />
                            )}
                          </IconBtn>
                          <IconBtn
                            onClick={handleCancelEdit}
                            className="w-6 h-6"
                          >
                            <XMarkIcon className="w-4 h-4 text-red-500 hover:text-red-600" />
                          </IconBtn>
                        </div>
                      ) : (
                        <>
                          <p className="text-gray-600 text-sm">{el.email}</p>
                          <SuperAdmin>
                            {el.status === 1 && el.esimProfileId && (
                              <IconBtn
                                onClick={() =>
                                  handleEditEmail(el.esimProfileId!, el.email)
                                }
                                className="w-6 h-6"
                              >
                                <PencilIcon className="w-4 h-4 text-blue-500 hover:text-blue-600" />
                              </IconBtn>
                            )}
                          </SuperAdmin>
                        </>
                      )}
                    </div>
                    <div className="mt-1.5 space-y-0.5">
                      <p className="text-gray-600 text-sm">
                        Paketa:{" "}
                        <span className="font-medium">{el.packageName}</span>
                      </p>
                      <p className="text-gray-600 text-sm">
                        Çmimi: <span className="font-medium">€{el.price}</span>
                      </p>
                      {el.uniqueJourneyId && (
                        <p className="text-gray-600 text-sm">
                          Blerje shtesë për rezervimin:{" "}
                          <a
                            href={`/ticket/reservations/edit/${el.uniqueJourneyId}`}
                            className="text-blue-500 hover:text-blue-600 font-medium"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {el.uniqueJourneyId}
                          </a>
                        </p>
                      )}
                      {el.status === 1 && el.esimProfileId && (
                        <Link
                          to={`/esim/profile/${el.esimProfileId}`}
                          className="text-blue-500 hover:text-blue-600 text-sm font-medium inline-block mt-1"
                        >
                          Shiko profilin
                        </Link>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex flex-col items-end h-full min-h-[90px]">
                    <div className="flex flex-col items-end">
                      <div
                        className={`px-2 py-0.5 rounded-full mb-1 text-sm text-center font-medium ${getStatusColor(
                          el.status
                        )}`}
                      >
                        <div className="w-fit flex justify-center items-center gap-1">
                          {el.status === 1 ? (
                            <>
                              {el.paymentMethodId === null && (
                                <span className="whitespace-nowrap">Admin</span>
                              )}
                              {el.paymentMethodId === 0 && (
                                <span className="whitespace-nowrap">Free</span>
                              )}
                              {el.paymentMethodId === 1 && (
                                <span className="whitespace-nowrap">RBKO</span>
                              )}
                              {el.paymentMethodId === 2 && (
                                <span className="whitespace-nowrap">
                                  Stripe
                                </span>
                              )}
                              {el.paymentMethodId === 7 && (
                                <span className="whitespace-nowrap">NLB</span>
                              )}
                              <CheckCircleIcon className="w-4 h-4" />
                            </>
                          ) : (
                            <>
                              {el.paymentMethodId === 1 && (
                                <span className="whitespace-nowrap">RBKO</span>
                              )}
                              {el.paymentMethodId === 2 && (
                                <span className="whitespace-nowrap">
                                  Stripe
                                </span>
                              )}
                              {el.paymentMethodId === 7 && (
                                <span className="whitespace-nowrap">NLB</span>
                              )}
                              <span className="whitespace-nowrap">
                                {getStatusLabel(el.status)}
                              </span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* eSIM Technical Status Badge */}
                      {el.status !== 5 &&
                        el.status !== 2 &&
                        el.esimProfileStatus && (
                          <>
                          <div
                            className={`w-fit flex justify-end px-2 py-0.5 rounded-full mb-1 text-sm
                            text-center font-medium cursor-pointer ${getEsimProfileStatusColor(
                              el.esimProfileStatus
                            )}`}
                            data-tooltip-content={getEsimProfileStatusDescription(
                              el.esimProfileStatus
                            )}
                            data-tooltip-id="statusDescription"
                          >
                            🛜 {getEsimProfileStatusLabel(el.esimProfileStatus)}
                          </div>
                          <Tooltip delayShow={2000} id="statusDescription" className="z-50" />
                          </>
                        )}
                      {!!el.amountRefunded && el.amountRefunded !== null && el.amountRefunded > 0 && (
                        <div className="w-fit flex justify-end px-2 py-0.5 rounded-full mb-1 text-sm text-center font-medium text-white bg-purple-500">
                          {el.isRefundInCredits ? "Credit" : "Cash"} refund: {el.amountRefunded.toFixed(2)}€
                        </div>
                      )}
                      <div className="flex items-center justify-end gap-2">
                        {el.isTopUp && (
                          <p className="mt-1 text-primary text-sm">⚡Top Up</p>
                        )}
                        <p className="text-xs font-bold px-2 py-1 my-1 rounded-2xl border border-black-900">
                          {el.sourceChannel === "MobileApp"
                            ? "📱"
                            : el.sourceChannel === "Web"
                            ? "💻"
                            : "🧑🏻‍💻"}{" "}
                          {el.sourceChannel}
                        </p>
                      </div>
                    </div>
                    <div className="flex-grow" />
                    <div className="flex flex-col items-end gap-1 mt-2">
                      <p className="text-sm text-gray-500">
                        {new Date(el.insertDate).toLocaleString("sq-AL", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit"
                        })}
                      </p>
                      <div className="flex items-center gap-1">
                        {el.status === 1 && (
                          <>
                            <DeleteModal
                              params={el.id}
                              title="Konfirmo ri-dërgimin e email-it"
                              paragraph="Ky veprim do të dërgojë përsëri email-in."
                              handleDelete={resendEsimReservationEmail}
                              successMessage="Email-i u dërgua përsëri me sukses"
                              errorMessage="Ndodhi një gabim gjatë dërgimit të email-it"
                              loadingMessage="Duke dërguar email-in..."
                              indicator={
                                <IconBtn className="w-8 h-8">
                                  <div data-tooltip-content="Përdoret për ridërgimin e email-it me të dhënat e eSIM-it tek klienti." data-tooltip-id="resendEmail">
                                  <EnvelopeIcon className="w-6 h-6 text-blue-500 hover:text-blue-600" />
                                  </div>
                                  <Tooltip id="resendEmail" className="z-50" />
                                </IconBtn>
                              }
                            />
                            <SuperAdmin>
                              <CancelEsimModal
                                uniqueReservationGuid={el.uniqueReservationGuid}
                                defaultRefundAmount={el.price}
                                onSuccess={() => refetch()}
                              />
                            </SuperAdmin>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          <FilteredListState
            loading={loading}
            error={listError}
            isEmpty={!response?.data.result.length}
            emptyText="Nuk u gjet asnjë rezervim eSIM me këto filtra."
            hasFilters={hasFilters}
            onClear={clearAll}
            onRetry={refetch}
          />
        </div>
        {response?.data && response.data.totalPages > 0 && (
          <div className="flex justify-center w-full mt-4">
            <Pagination
              onPageChange={handlePageChange}
              totalCount={response.data.totalCount || 0}
              page={page}
              length={response.data.result.length}
              pageSize={response.data.pageSize || 10}
              viewingOf={false}
              className="border-none px-0 py-0"
            />
          </div>
        )}
      </div>
      <Modal
        hasHeader={false}
        show={showStatusLegend}
        position={"center"}
        onClose={() => setShowStatusLegend(false)}
        modalWidth="650px"
      >
        <div className="p-4 flex flex-col gap-3">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-semibold">Përshkrimi e statuseve</h3>
            <IconBtn onClick={() => setShowStatusLegend(false)}>
              <XMarkIcon className="w-5 h-5" />
            </IconBtn>
          </div>
          {ESIM_STATUS_KEYS.map((key) => (
            <div key={key} className="flex items-center gap-2 border-b pb-2 last:border-b-0 last:pb-0">
              <div
                className={`w-fit flex items-center gap-2 px-2 py-0.5 rounded-full text-sm font-medium ${getEsimProfileStatusColor(
                  key
                )}`}
              >
                🛜 <p className="text-sm whitespace-nowrap">{getEsimProfileStatusLabel(key)}</p>
              </div>
              <p className="text-sm font-medium">
                {getEsimProfileStatusDescription(key)}
              </p>
            </div>
          ))}
          <div  className="flex items-center gap-2">
              <div
                className="w-fit flex items-center gap-2 px-2 py-0.5 rounded-full text-sm font-medium"
              >
                <EnvelopeIcon className="w-6 h-6 text-blue-500 hover:text-blue-600" />
              </div>
              <p className="text-sm font-medium">
                Përdoret për ridërgimin e email-it me të dhënat e eSIM-it tek klienti.
              </p>
            </div>
        </div>
      </Modal>

      {/* Resend Confirmation Modal */}
      <Modal
        position="center"
        show={showResendModal}
        onClose={handleCloseModal}
        mini={true}
        hasHeader={false}
        calcHeight={false}
        title="Konfirmo ri-dërgimin"
        buttonElement={
          <Button
            onClick={handleResendEmail}
            disabled={isResendingEmail}
            className="flex items-center gap-2"
          >
            {isResendingEmail && (
              <ArrowPathIcon className="w-4 h-4 animate-spin" />
            )}
            Po, dërgo përsëri
          </Button>
        }
      >
        <div className="flex justify-center mt-6">
          <h3 className="text-lg font-semibold text-center">
            Konfirmo ri-dërgimin
          </h3>
          <IconBtn
            className="absolute right-2 top-2"
            onClick={handleCloseModal}
          >
            <XMarkIcon className="w-4 h-4 text-neutral" strokeWidth={2} />
          </IconBtn>
        </div>
        <p className="justify-center w-full flex mt-6 text-gray-600 text-sm text-center px-4">
          Email-i u ndryshua me sukses. Dëshiron të dërgosh përsëri email-in e
          konfirmimit të eSIM-it?
        </p>
      </Modal>
    </>
  );
};

export default EsimReservations;
